import { useRef, useEffect, use } from 'react';
import {
  format as dateFnFormat,
  subMilliseconds,
  addMilliseconds,
} from 'date-fns';
import { enGB } from 'date-fns/locale';

import { TimezoneContext } from '@/modules/date-time/contexts/TimezoneContext';

export { parseJSON } from 'date-fns';

const MILLISECONDS_IN_MINUTE = 60000;

export function format(
  date: Date,
  formatStr: string,
  { timezone }: { timezone?: string } = {},
) {
  if (timezone) {
    date = asLocalTime(date, timezone);
  }

  return dateFnFormat(date, formatStr, { locale: enGB });
}

interface TimezoneConverter {
  (date: Date, timezone: string): Date;
  (date: null, timezone: string): null;
  (date: Date | null, timezone: string): Date | null;
}

interface WrappedTimezoneConverter {
  (date: Date): Date;
  (date: null): null;
  (date: Date | null): Date | null;
}

export const TIMEZONE_SUPPORTED_IN_BROWSER = timezoneSupported();

function timezoneSupported() {
  try {
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Australia/Sydney',
      timeZoneName: 'long',
    }).format();
    return true;
  } catch {
    return false;
  }
}

export function useTimezoneUtilsRef() {
  const timezone = use(TimezoneContext);

  const withTimezone = (func: TimezoneConverter) => {
    return ((date: Date | null) =>
      func(date, timezone || '')) as WrappedTimezoneConverter;
  };

  const utils = {
    asUniversalTime: withTimezone(asUniversalTime as TimezoneConverter), // Updated type of asUniversalTime
    asLocalTime: withTimezone(asLocalTime as TimezoneConverter), // Updated type of asLocalTime
  };

  const ref = useRef(utils);

  useEffect(() => {
    ref.current = utils;
  });

  return ref;
}

// Take a date in the user's local time and turn it into
// the same time in the given timezone (e.g. BST). So 5pm local goes to 5pm BST
// NOTE: There's an intractable timezone issue here. Some date/times don't exist
// e.g. 01:30 on the day the clocks go forwards, but may exist in the local time
// Ignore it for now and hope it doesn't really affect us...
function asUniversalTime(date: Date, timezone: string): Date;
function asUniversalTime(date: null, timezone: string): null;
function asUniversalTime(date: Date | null, timezone: string) {
  if (!date) {
    return date;
  }

  // If we don't support timezones in this browser just return the original date
  if (!TIMEZONE_SUPPORTED_IN_BROWSER) {
    return date;
  }

  // If the local and universal timezone are the same then just return the given date
  const localTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!localTimezone) {
    return date;
  }

  if (localTimezone.toLowerCase() === timezone.toLowerCase()) {
    return date;
  }

  const localOffset = getTimezoneOffsetInMilliseconds(date);
  const utcDate = subMilliseconds(date, localOffset); // same date in utc time
  const timezoneOffset = getSpecifiedTimezoneOffsetInMilliseconds(
    utcDate,
    timezone,
  );
  return addMilliseconds(utcDate, timezoneOffset);
}

function asLocalTime(date: Date, timezone: string): Date;
function asLocalTime(date: null, timezone: string): null;
function asLocalTime(date: Date | null, timezone: string) {
  if (!date) {
    return date;
  }

  // If we don't support timezones in this browser just return the original date
  if (!TIMEZONE_SUPPORTED_IN_BROWSER) {
    return date;
  }

  // If the local and universal timezone are the same then just return the given date
  const localTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!localTimezone) {
    return date;
  }

  if (localTimezone.toLowerCase() === timezone.toLowerCase()) {
    return date;
  }

  const timezoneOffset = getSpecifiedTimezoneOffsetInMilliseconds(
    date,
    timezone,
  );
  const utcDate = subMilliseconds(date, timezoneOffset);
  const localOffset = getTimezoneOffsetInMilliseconds(utcDate);
  return addMilliseconds(utcDate, localOffset);
}

// Nabbed from date-fns
/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(dirtyDate: Date) {
  const date = new Date(dirtyDate.getTime());
  const baseTimezoneOffset = date.getTimezoneOffset();
  date.setSeconds(0, 0);
  const millisecondsPartOfTimezoneOffset =
    date.getTime() % MILLISECONDS_IN_MINUTE;

  return (
    baseTimezoneOffset * MILLISECONDS_IN_MINUTE +
    millisecondsPartOfTimezoneOffset
  );
}

function getDateTimeFormat(timeZone: string) {
  return new Intl.DateTimeFormat('en-US', {
    hour12: false,
    timeZone: timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getSpecifiedTimezoneOffsetInMilliseconds(
  date: Date,
  timeZone: string,
) {
  const dtf = getDateTimeFormat(timeZone);
  const formatted = dtf.formatToParts(date);

  const parsedVals = {
    year: 0,
    month: 0,
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
  };

  for (const val of formatted) {
    if (parsedVals[val.type as keyof typeof parsedVals] === 0) {
      parsedVals[val.type as keyof typeof parsedVals] = parseInt(val.value, 10);
    }
  }

  const asUTC = Date.UTC(
    parsedVals.year,
    parsedVals.month - 1, // 0 indexed,
    parsedVals.day,
    parsedVals.hour,
    parsedVals.minute,
    parsedVals.second,
  );

  const dateWithMsZeroed = date.getTime() - (date.getTime() % 1000);
  return dateWithMsZeroed - asUTC;
}

export const getShortDate = (inputDate: string | null) => {
  if (!inputDate) {
    return '';
  }

  const formatString = 'do LLL yyyy';
  const date = new Date(inputDate);

  return format(date, formatString);
};
